import React, { useContext } from 'react';
import {
    Box,
    Typography,
    Grid,
    Avatar,
    Divider,
    Chip,
    Stack,
} from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { AuthContext } from '../../context/logeduser';

// ---- FORTHEYE soft-card tokens (shared with RoleManagement / the rest of the portal) ----
const CARD_SX = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: '22px',
    bgcolor: '#fff',
    border: '1px solid #eef0f4',
    boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

// Same module list RoleManagement uses, so the permissions shown here match
// exactly what an HR contact can see/edit elsewhere in the app.
const MODULES = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'employees', label: 'Employee Management' },
    { id: 'leaves', label: 'Leave Requests' },
    { id: 'recruitment', label: 'Recruitment (Candidates)' },
    { id: 'payroll', label: 'Payroll Management' },
    { id: 'master_records', label: 'Master Records' },
    { id: 'settings', label: 'Global Settings' },
];

// A compact "granted / not granted" pill used inside each permission tile.
const StatusPill = ({ label, granted }) => (
    <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
            px: 1.25,
            py: 0.5,
            borderRadius: '999px',
            bgcolor: granted ? '#ecfdf5' : '#f3f4f6',
            border: `1px solid ${granted ? '#bbf7d0' : '#e5e7eb'}`,
            flex: 1,
            justifyContent: 'center',
        }}
    >
        {granted
            ? <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
            : <CancelIcon sx={{ color: '#d1d5db', fontSize: 18 }} />}
        <Typography variant="caption" sx={{ fontWeight: 700, color: granted ? '#15803d' : '#9ca3af' }}>
            {label}
        </Typography>
    </Stack>
);

// One stat-tile per module: label up top, View/Edit pills laid out side by side
// underneath so the wide right-hand panel gets real content density instead
// of one cramped table.
const PermissionTile = ({ label, view, edit }) => (
    <Box
        sx={{
            height: '100%',
            border: '1px solid #eef0f4',
            borderRadius: '16px',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            bgcolor: '#fafbfc',
        }}
    >
        <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        <Stack direction="row" spacing={1}>
            <StatusPill label="View" granted={view} />
            <StatusPill label="Edit" granted={edit} />
        </Stack>
    </Box>
);

const MyProfile = () => {
    const { authData } = useContext(AuthContext);
    const admin = authData?.admin || {};

    const isGlobalAdmin = admin.email === 'admin@atshub.in';
    const branches = Array.isArray(admin.branches) && admin.branches.length
        ? admin.branches
        : (admin.branch ? [admin.branch] : []);
    const permissions = admin.permissions || {};

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>My Profile</Typography>

            <Grid container spacing={3} alignItems="stretch">
                {/* LEFT: profile summary card (~1/3 width on desktop) */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ ...CARD_SX, height: '100%' }}>
                        <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', mb: 3 }}>
                            <Avatar sx={{ width: 84, height: 84, bgcolor: ACCENT, fontSize: 36, fontWeight: 700 }}>
                                {(admin.user_name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{admin.user_name || 'Unknown'}</Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                                    <Chip
                                        size="small"
                                        icon={<AdminPanelSettingsIcon />}
                                        label={isGlobalAdmin ? 'Global Admin' : 'HR Contact'}
                                        sx={{ fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT, border: 'none' }}
                                    />
                                    {admin.is_restricted && (
                                        <Chip
                                            size="small"
                                            label="Restricted"
                                            sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', border: 'none' }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        </Stack>

                        <Divider sx={{ mb: 3 }} />

                        <Stack spacing={2.5}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <EmailIcon sx={{ color: '#9ca3af', mt: 0.3 }} />
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>
                                        Email
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{admin.email || '—'}</Typography>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <BadgeIcon sx={{ color: '#9ca3af', mt: 0.3 }} />
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>
                                        Account ID
                                    </Typography>
                                    <Typography sx={{ fontWeight: 600 }}>{admin.id ?? '—'}</Typography>
                                </Box>
                            </Stack>

                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <BusinessIcon sx={{ color: '#9ca3af', mt: 0.3 }} />
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>
                                        Branch(es) Assigned
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        {isGlobalAdmin ? (
                                            <Chip size="small" label="All Branches (Global Access)" sx={{ fontWeight: 700, bgcolor: '#eef2ff', color: '#4338ca', border: 'none' }} />
                                        ) : branches.length ? (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                                                {branches.map((b) => (
                                                    <Chip key={b} size="small" label={b} sx={{ fontWeight: 700, bgcolor: `${ACCENT}14`, color: ACCENT, border: 'none' }} />
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography sx={{ color: '#9ca3af', fontStyle: 'italic' }}>No branch assigned</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>
                </Grid>

                {/* RIGHT: module permissions, given the wide two-thirds panel */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ ...CARD_SX, height: '100%' }}>
                        <Typography sx={{ fontWeight: 700, mb: 2 }}>Module Permissions</Typography>
                        {isGlobalAdmin ? (
                            <Box
                                sx={{
                                    p: 3,
                                    borderRadius: '16px',
                                    bgcolor: '#eef2ff',
                                    border: '1px solid #e0e7ff',
                                }}
                            >
                                <Typography sx={{ color: '#4338ca', fontWeight: 600 }}>
                                    Global Admin has full view &amp; edit access to every module.
                                </Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2}>
                                {MODULES.map((m) => (
                                    <Grid item xs={12} sm={6} lg={4} key={m.id}>
                                        <PermissionTile
                                            label={m.label}
                                            view={!!permissions?.[m.id]?.view}
                                            edit={!!permissions?.[m.id]?.edit}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default MyProfile;
